import { useCallback } from 'react';
import { TimezoneDate } from '../../types';

const MINUTE_IN_MILLISECONDS = 60_000;
const HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60;

const dateParseRegex =
    /^(?<year>\d{4})\/(?<month>\d{1,2})\/(?<day>\d{1,2}) (?<hour>\d{1,2}):(?<minute>\d{1,2}):(?<second>\d{1,2}) GMT((?<offsetDirection>[+-])(?<offsetHours>\d{1,2}):(?<offsetMinutes>\d{1,2}))?$/;

export const parseTimezoneDate = (date: Date, timezone: string): TimezoneDate => {
    const timezoneString = new Intl.DateTimeFormat('ja', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'longOffset',
    }).format(date);
    const match = dateParseRegex.exec(timezoneString);
    if (!match) {
        throw new Error('Bad date');
    }
    const {
        year,
        month,
        day,
        hour,
        minute,
        second,
        offsetDirection = '+',
        offsetHours = '0',
        offsetMinutes = '0',
    } = match.groups || {};

    const offsetMilliseconds =
        Number(offsetHours) * HOUR_IN_MILLISECONDS + Number(offsetMinutes) * MINUTE_IN_MILLISECONDS;
    const timezoneUTCOffset = offsetDirection === '-' ? -offsetMilliseconds : offsetMilliseconds;
    const localUTCOffset = date.getTimezoneOffset() * MINUTE_IN_MILLISECONDS;
    const timezoneOffset = localUTCOffset * -1 + timezoneUTCOffset * -1;

    return {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
        second: Number(second),
        timezoneUTCOffset: timezoneUTCOffset,
        localUTCOffset: localUTCOffset,
        timezoneOffset: timezoneOffset,
        dayOfWeek: new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0).getDay(),
    };
};

export const useTimezone = (timezone: string) => {
    const parseDate = useCallback(
        (date: Date): TimezoneDate => {
            return parseTimezoneDate(date, timezone);
        },
        [timezone]
    );

    return {
        parseDate,
    };
};
